import { createFileRoute, redirect } from "@tanstack/react-router";

import { activeOrganization$ } from "~/stores/organizations";

/**
 * App-shell gate. Two columns drive entry:
 *
 * - `user.onboarding !== "done"` -> the user has personal setup to finish
 *   (profile, workspace decision). Bounce to `/onboard`.
 * - `org.onboarding !== "done"` -> the active workspace hasn't finished
 *   onboarding. Since payment is the last step before `done`, this is also
 *   the credit-card paywall: any non-`done` value bounces to `/onboard`,
 *   which resumes the user at the right step (datasource, catalog, sample,
 *   or plan). Non-owners on a mid-flow org see the "waiting for owner"
 *   screen on the plan step.
 *
 * Reading `activeOrganization$` directly is safe here because the
 * better-auth atom is populated as soon as the session resolves; the
 * Legend State store also persists to localStorage so refreshes see the
 * last-known value synchronously. An absent active org is not blocked --
 * the `_app/index` triage forwards the no-org case to `/onboard`.
 */
export const Route = createFileRoute("/_app")({
  beforeLoad: ({ context }) => {
    if (!context.user) {
      throw redirect({ to: "/login", replace: true });
    }
    if (context.user.onboarding && context.user.onboarding !== "done") {
      throw redirect({ to: "/onboard", replace: true });
    }
    const org = activeOrganization$.get();
    if (org?.id && org.onboarding !== "done") {
      throw redirect({ to: "/onboard", replace: true });
    }
  },
  // No `component`: a layout route without one defaults to rendering its
  // `<Outlet />`. The authenticated `<App>` shell is hoisted to `<AppShell>`
  // in `__root.tsx`, so `/_app/*` children render inside that single,
  // persistent `<App>` instance.
});
